"use client"

import type { FunctionComponent, PropsWithChildren } from "react"

import { useEffect, useRef, useState } from "react"

import { IconButton } from "@jobteaser/spark/components/IconButton"
import { ChevronLeft } from "@jobteaser/spark/components/Icons/ChevronLeft"
import { ChevronRight } from "@jobteaser/spark/components/Icons/ChevronRight"

import styles from "./PinnedEvents.module.css"

type InteractiveListProps = PropsWithChildren<{
  nextButtonLabel: string
  previousButtonLabel: string
}>

export const InteractiveList: FunctionComponent<InteractiveListProps> = ({
  children,
  nextButtonLabel,
  previousButtonLabel,
}) => {
  const ref = useRef<HTMLUListElement>(null)
  const [isPreviousButtonActive, setPreviousButtonActive] = useState(true)
  const [isNextButtonActive, setNextButtonActive] = useState(true)

  function checkButtonActiveStatus(): void {
    if (ref.current && ref.current.scrollLeft === 0) {
      setPreviousButtonActive(false)
    } else {
      setPreviousButtonActive(true)
    }

    if (ref.current && ref.current.scrollLeft + ref.current.offsetWidth === ref.current.scrollWidth) {
      setNextButtonActive(false)
    } else {
      setNextButtonActive(true)
    }
  }

  useEffect(() => {
    checkButtonActiveStatus()

    const container = ref.current
    container?.addEventListener("scrollend", checkButtonActiveStatus)

    return () => {
      container?.removeEventListener("scrollend", checkButtonActiveStatus)
    }
  }, [])

  const handleClick = (direction: "left" | "right"): void => {
    if (!ref.current || !ref.current.firstChild) {
      return
    }

    const itemWidth = (ref.current.firstChild as HTMLElement).offsetWidth
    const scrollByAmount = direction === "left" ? -1 * itemWidth : itemWidth

    ref.current.scrollBy({
      behavior: "smooth",
      left: scrollByAmount,
    })
  }

  return (
    <>
      <ul className={styles.list} ref={ref}>
        {children}
      </ul>
      <IconButton
        aria-hidden="true"
        disabled={!isPreviousButtonActive}
        className={styles.previousButton}
        icon={ChevronLeft}
        onClick={() => handleClick("left")}
        title={previousButtonLabel}
        variant="transparent"
      />
      <IconButton
        aria-hidden="true"
        disabled={!isNextButtonActive}
        className={styles.nextButton}
        icon={ChevronRight}
        onClick={() => handleClick("right")}
        title={nextButtonLabel}
        variant="transparent"
      />
    </>
  )
}
